import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DoCheck,
  effect,
  forwardRef,
  inject,
  input,
  linkedSignal,
  Optional,
  Self,
  signal,
  untracked,
} from "@angular/core";
import {
  ControlValueAccessor,
  FormGroupDirective,
  NgControl,
  NgForm,
} from "@angular/forms";
import { BrnFormFieldControl } from "@spartan-ng/brain/form-field";
import { ErrorStateMatcher, ErrorStateTracker } from "@spartan-ng/brain/forms";
import type { ClassValue } from "clsx";
import {
  HlmInputGroup,
  HlmInputGroupAddon,
  HlmInputGroupButton,
  HlmInputGroupInput,
} from "@o2-fe/ui";
import { HlmIcon } from "@o2-fe/ui";
import { NgIcon, provideIcons } from "@ng-icons/core";
import { lucideEye, lucideEyeOff } from "@ng-icons/lucide";
import { HlmButtonImports } from "@o2-fe/ui";

/**
 * O2 Password Input Component
 *
 * A reusable password input component with show/hide toggle functionality.
 * Fully integrates with Angular Forms and Spartan UI components.
 *
 * Features:
 * - Password visibility toggle (eye icon)
 * - Angular Forms integration via ControlValueAccessor
 * - Automatic validation error styling
 * - Works inside hlm-form-field for error messages
 * - Consistent styling with Spartan UI components
 *
 * @example
 * Basic usage:
 * ```html
 * <label hlmLabel>
 *   Password
 *   <o2-password-input
 *     formControlName="password"
 *     placeholder="Enter password"
 *   />
 * </label>
 * ```
 *
 * @example
 * With form field validation:
 * ```html
 * <hlm-form-field>
 *   <o2-password-input
 *     formControlName="password"
 *     placeholder="Enter password"
 *   />
 *   <hlm-error>Password is required</hlm-error>
 * </hlm-form-field>
 * ```
 */
@Component({
  selector: "o2-password-input",
  standalone: true,
  imports: [
    HlmInputGroup,
    HlmInputGroupButton,
    HlmInputGroupInput,
    HlmInputGroupAddon,
    HlmIcon,
    NgIcon,
    HlmButtonImports,
  ],
  providers: [
    {
      provide: BrnFormFieldControl,
      useExisting: forwardRef(() => O2PasswordInputComponent),
    },
    provideIcons({ lucideEye, lucideEyeOff }),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div hlmInputGroup>
      <input
        hlmInputGroupInput
        [type]="_inputType()"
        [placeholder]="placeholder()"
        [disabled]="disabled()"
        [class]="userClass()"
        [value]="_value()"
        (input)="_onInput($event)"
        (blur)="_onTouched()"
      />
      <div hlmInputGroupAddon align="inline-end">
        <button
          hlmInputGroupButton
          variant="ghost"
          size="icon-xs"
          [disabled]="disabled()"
          (click)="_togglePasswordVisibility()"
          [attr.aria-label]="
            _isPasswordVisible() ? 'Hide password' : 'Show password'
          "
          tabindex="-1"
        >
          <ng-icon hlm [name]="_toggleIcon()" tabindex="-1" size="sm" />
        </button>
      </div>
    </div>
  `,
})
export class O2PasswordInputComponent
  implements ControlValueAccessor, BrnFormFieldControl, DoCheck
{
  // ============================================================================
  // Inputs
  // ============================================================================

  /**
   * Placeholder text for the input
   */
  public readonly placeholder = input<string>("");

  /**
   * Disabled state of the input
   */
  public readonly disabled = input<boolean>(false);

  /**
   * CSS class to apply to the input element
   */
  public readonly userClass = input<ClassValue>("", { alias: "class" });

  // ============================================================================
  // Internal State
  // ============================================================================

  /**
   * Current input value
   */
  protected readonly _value = signal<string>("");

  /**
   * Password visibility toggle state
   */
  protected readonly _isPasswordVisible = signal<boolean>(false);

  /**
   * Computed input type based on visibility state
   */
  protected readonly _inputType = computed(() =>
    this._isPasswordVisible() ? "text" : "password",
  );

  /**
   * Computed icon name based on visibility state
   */
  protected readonly _toggleIcon = computed(() =>
    this._isPasswordVisible() ? "lucideEyeOff" : "lucideEye",
  );

  // ============================================================================
  // BrnFormFieldControl Implementation
  // ============================================================================

  /**
   * Reference to the NgControl (FormControl) if available
   */
  public readonly ngControl: NgControl | null;

  /**
   * Error state tracker for validation
   */
  private readonly _errorStateTracker: ErrorStateTracker;

  private readonly _defaultErrorStateMatcher = inject(ErrorStateMatcher);
  private readonly _parentForm = inject(NgForm, { optional: true });
  private readonly _parentFormGroup = inject(FormGroupDirective, {
    optional: true,
  });

  /**
   * Error state signal - used by hlm-form-field
   */
  public readonly errorState = computed(() =>
    this._errorStateTracker.errorState(),
  );

  /**
   * Internal error state for styling
   */
  protected readonly _state = linkedSignal<{ error: "auto" | true }>(() => ({
    error: "auto",
  }));

  // ============================================================================
  // ControlValueAccessor Callbacks
  // ============================================================================

  private _onChange: (value: string) => void = () => {};
  private _onTouchedCallback: () => void = () => {};

  // ============================================================================
  // Constructor
  // ============================================================================

  constructor(@Self() @Optional() ngControl: NgControl | null) {
    // Set the value accessor for this control
    this.ngControl = ngControl;
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    // Initialize error state tracker
    this._errorStateTracker = new ErrorStateTracker(
      this._defaultErrorStateMatcher,
      this.ngControl,
      this._parentFormGroup,
      this._parentForm,
    );

    // Effect to sync error state for styling
    effect(() => {
      const error = this._errorStateTracker.errorState();
      untracked(() => {
        if (this.ngControl) {
          const shouldShowError =
            error &&
            this.ngControl.invalid &&
            (this.ngControl.touched || this.ngControl.dirty);
          this._errorStateTracker.errorState.set(
            shouldShowError ? true : false,
          );
          this._state.set({ error: shouldShowError ? true : "auto" });
        }
      });
    });
  }

  // ============================================================================
  // DoCheck Implementation
  // ============================================================================

  /**
   * Update error state on each change detection cycle
   */
  ngDoCheck(): void {
    this._errorStateTracker.updateErrorState();
  }

  // ============================================================================
  // ControlValueAccessor Implementation
  // ============================================================================

  /**
   * Write value from form model to component
   * @param value - The value to write
   */
  writeValue(value: string | null): void {
    this._value.set(value ?? "");
  }

  /**
   * Register callback for value changes
   * @param fn - The callback function
   */
  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  /**
   * Register callback for touch events
   * @param fn - The callback function
   */
  registerOnTouched(fn: () => void): void {
    this._onTouchedCallback = fn;
  }

  /**
   * Set disabled state
   * @param isDisabled - Whether the input should be disabled
   */
  setDisabledState(isDisabled: boolean): void {
    // Disabled state is managed through the input signal
    // Angular Forms will call this method when the control's disabled state changes
  }

  // ============================================================================
  // Event Handlers
  // ============================================================================

  /**
   * Handle input events
   * @param event - The input event
   */
  protected _onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    this._value.set(value);
    this._onChange(value);
  }

  /**
   * Handle blur events
   */
  protected _onTouched(): void {
    this._onTouchedCallback();
  }

  /**
   * Toggle password visibility
   */
  protected _togglePasswordVisibility(): void {
    this._isPasswordVisible.update((visible) => !visible);
  }
}
